import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useShop, itemKey } from "../context/ShopContext";
import { money } from "../lib/data";
import CheckoutModal from "./CheckoutModal";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeItem, subtotal } = useShop();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const reduce = useReducedMotion();

  return (
    <>
      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              data-testid="cart-overlay"
              className="fixed inset-0 z-[75] bg-black/70"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setCartOpen(false)}
            />
            <motion.aside
              data-testid="cart-drawer"
              className="fixed right-0 top-0 z-[76] flex h-full w-full max-w-md flex-col border-l border-ll-line bg-ll-coal"
              initial={reduce ? { opacity: 0 } : { x: "100%" }}
              animate={reduce ? { opacity: 1 } : { x: 0 }}
              exit={reduce ? { opacity: 0 } : { x: "100%" }}
              transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
              role="dialog" aria-label="Shopping cart"
            >
              <div className="flex items-center justify-between border-b border-ll-line px-6 py-5">
                <p className="font-display text-2xl tracking-[0.1em]">YOUR CART</p>
                <button data-testid="cart-close-button" onClick={() => setCartOpen(false)} aria-label="Close cart">
                  <X className="h-6 w-6 transition-colors duration-200 hover:text-ll-red" />
                </button>
              </div>

              {cart.length === 0 ? (
                <div data-testid="cart-empty-state" className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                  <p className="font-display text-3xl text-ll-steel">CART IS EMPTY</p>
                  <p className="max-w-xs text-sm text-ll-steel">The shelf is stocked and shipping. Find the piece that changes your room.</p>
                  <a data-testid="cart-empty-shop-button" href="/shop" onClick={() => setCartOpen(false)}
                     className="mt-2 border border-ll-bone px-8 py-3 font-tech text-xs tracking-[0.2em] transition-colors duration-200 hover:bg-ll-bone hover:text-ll-black">
                    BROWSE THE SHELF
                  </a>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    {cart.map((it) => {
                      const key = itemKey(it);
                      const tid = it.variant ? `${it.slug}-${it.variant.toLowerCase().replace(/\s+/g, "-")}` : it.slug;
                      return (
                      <motion.div layout key={key} data-testid={`cart-item-${tid}`}
                                  className="flex gap-4 border-b border-ll-line py-4">
                        <img src={it.image} alt={it.name} className="h-24 w-20 object-cover" loading="lazy" />
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold leading-tight">{it.name}</p>
                              {it.variant && <p data-testid={`cart-variant-${tid}`} className="mt-0.5 font-tech text-[10px] tracking-[0.2em] text-ll-steel">{it.variant.toUpperCase()}</p>}
                            </div>
                            <button data-testid={`cart-remove-${tid}`} onClick={() => removeItem(key)} aria-label={`Remove ${it.name}`}>
                              <Trash2 className="h-4 w-4 text-ll-steel transition-colors duration-200 hover:text-ll-red" />
                            </button>
                          </div>
                          <div className="mt-auto flex items-center justify-between pt-2">
                            <div className="flex items-center border border-ll-linestrong">
                              <button data-testid={`cart-qty-minus-${tid}`} onClick={() => updateQty(key, it.qty - 1)} className="px-2 py-1" aria-label="Decrease quantity">
                                <Minus className="h-3 w-3" />
                              </button>
                              <span data-testid={`cart-qty-${tid}`} className="w-8 text-center font-tech text-xs">{it.qty}</span>
                              <button data-testid={`cart-qty-plus-${tid}`} onClick={() => updateQty(key, it.qty + 1)} className="px-2 py-1" aria-label="Increase quantity">
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                            <p className="font-tech text-sm">{money(it.qty * it.price)}</p>
                          </div>
                        </div>
                      </motion.div>
                      );
                    })}
                  </div>

                  <div className="border-t border-ll-line px-6 py-5">
                    <p className="border border-ll-linestrong bg-ll-black px-3 py-2 text-[11px] leading-relaxed text-ll-bone/90">
                      You'll get a confirmation email with tracking the moment your order ships.
                    </p>
                    <div className="mt-4 flex items-center justify-between">
                      <p className="font-tech text-xs tracking-[0.2em] text-ll-steel">SUBTOTAL</p>
                      <p data-testid="cart-subtotal" className="font-display text-2xl">{money(subtotal)}</p>
                    </div>
                    <p className="mt-1 text-right text-[11px] text-ll-steel">Shipping calculated at checkout</p>
                    <motion.button
                      data-testid="cart-checkout-button"
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setCheckoutOpen(true)}
                      className="mt-4 w-full bg-ll-red py-4 font-tech text-sm tracking-[0.25em] text-ll-bone transition-colors duration-200 hover:bg-ll-redhover"
                    >
                      CHECKOUT
                    </motion.button>
                  </div>
                </>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
      <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
    </>
  );
}
